import { z } from 'zod';
import i18next from '@/lib/i18n';

/**
 * Client-side validation schemas for the initial-setup Excel uploads.
 * They mirror the backend contract schemas (InitializeOrgFunctionInput,
 * InitializeMembershipInput, InitializeSystemInput from @bliks-io/contracts).
 *
 * Factory functions are used to ensure validation messages
 * are translated at validation time, not at import time.
 */

/**
 * Create an org-function/team row schema with translated error messages.
 * The row carries no kind — kind is fixed by which template the user
 * downloaded (ADR 0004).
 */
export function createOrgFunctionRowSchema() {
  return z.object({
    name: z.string().min(1, i18next.t('bulkOnboarding.validation.nameRequired')),
    description: z.string().optional(),
    department: z.string().optional(),
    leadEmail: z.email(i18next.t('bulkOnboarding.validation.invalidEmail')).optional(),
    email: z.email(i18next.t('bulkOnboarding.validation.invalidEmail')).optional()
  });
}

/**
 * Create a membership row schema with translated error messages
 */
export function createMembershipRowSchema() {
  return z.object({
    orgFunctionName: z
      .string()
      .min(1, i18next.t('bulkOnboarding.validation.orgFunctionNameRequired')),
    memberEmail: z.email(i18next.t('bulkOnboarding.validation.invalidEmail'))
  });
}

/**
 * Create a system row schema with translated error messages
 */
export function createSystemRowSchema() {
  const licenceSeatsMessage = i18next.t('bulkOnboarding.validation.licenceSeatsInvalid');
  return z.object({
    name: z.string().min(1, i18next.t('bulkOnboarding.validation.nameRequired')),
    description: z.string().optional(),
    category: z.string().optional(),
    url: z.string().optional(),
    licenceSeats: z
      .number(licenceSeatsMessage)
      .int(licenceSeatsMessage)
      .nonnegative(licenceSeatsMessage)
      .optional(),
    ownerEmail: z.email(i18next.t('bulkOnboarding.validation.invalidEmail'))
  });
}

/**
 * Create a user row schema with translated error messages: the users upload
 * of the initial setup and its draft table
 */
export function createUserSchema() {
  return z.object({
    firstName: z.string().min(1, i18next.t('bulkOnboarding.validation.firstNameRequired')),
    lastName: z.string().min(1, i18next.t('bulkOnboarding.validation.lastNameRequired')),
    email: z.string().email(i18next.t('bulkOnboarding.validation.invalidEmail')),
    department: z.string().min(1, i18next.t('bulkOnboarding.validation.departmentRequired')),
    role: z.string().min(1, i18next.t('bulkOnboarding.validation.roleRequired')),
    roleDescription: z.string().optional()
  });
}

/**
 * A row of the Excel data source upload: the user row plus the Bliks id
 * and Standort columns. Department, Stelle and Standort may be empty, as the
 * download writes them for people without one: a missing source value never
 * proposes clearing.
 */
export function createDataSyncUserSchema() {
  return createUserSchema().omit({ roleDescription: true }).extend({
    id: z.string().optional(),
    department: z.string().optional(),
    role: z.string().optional(),
    location: z.string().optional()
  });
}

// Type exports derived from schemas
export type OrgFunctionRowInput = z.infer<ReturnType<typeof createOrgFunctionRowSchema>>;
export type MembershipRowInput = z.infer<ReturnType<typeof createMembershipRowSchema>>;
export type SystemRowInput = z.infer<ReturnType<typeof createSystemRowSchema>>;
export type UserInput = z.infer<ReturnType<typeof createUserSchema>>;
export type DataSyncUserInput = z.infer<ReturnType<typeof createDataSyncUserSchema>>;
